import React from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';

import { AppRedirect, routeNaming, goToPage } from '../../routes';
import { CheckoutNavbar } from '../../common/checkout-navbar';
import { SessionStore } from '../../stores/SessionStore';
import { CartStore } from '../../stores/CartStore';
import { CheckoutStore, CHECKOUT_STAGES } from '../../stores/CheckoutStore';
import { ChooseAddress } from '../choose-address';
import { CheckoutSuccess } from '../checkout-success';
import { ShippingOptions } from '../shipping-options';
import { InstallationOptions } from '../installation-options';
import { ConfirmCheckout } from '../confirm-checkout';
import { PaymentMethodsAndBilling } from '../payment-methods-and-billing';
import styles from './CheckoutCoordinator.css';
import { GA4 } from '../../helpers/GA4';
import { ConfigParametersStore } from '../../stores/ConfigParametersStore';

const IPropTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      checkoutStep: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

class CheckoutCoordinator extends React.Component {
  static goToStage(stage) {
    goToPage(routeNaming.CHECKOUT, { checkoutStep: stage });
  }

  static onPickupSelection() {
    CheckoutStore.selectPickup();
    CheckoutCoordinator.goToStage(CHECKOUT_STAGES.SHIPPING_OPTIONS);
  }

  static async onAddressSelection(addressId) {
    await CheckoutStore.useAddress(addressId);
    if (CheckoutStore.selectedAddress) {
      CheckoutCoordinator.goToStage(CHECKOUT_STAGES.SHIPPING_OPTIONS);
    }
  }

  static onShippingOptionSubmit(firstPhone, secondPhone, option, selectedTimeSlot, selectedAgency) {
    CheckoutStore.useShipping(firstPhone, secondPhone, option, selectedTimeSlot, selectedAgency);
    const hasArticleInPresale = CheckoutStore.checkoutCart
      .map(item => item.product.id)
      .some(item => ConfigParametersStore.isValueInFeatureFlagData('product_next_deliveries', item));
    if (CheckoutStore.cartHasInstallations && !CheckoutStore.pickupChosen && !hasArticleInPresale) {
      CheckoutCoordinator.goToStage(CHECKOUT_STAGES.INSTALLATION_OPTIONS);
    } else {
      CheckoutCoordinator.goToStage(CHECKOUT_STAGES.BILLING_OPTIONS);
    }
  }

  static async onInstallationSubmit(installationOptions) {
    CheckoutStore.useInstallationOptions(installationOptions);
    CheckoutCoordinator.goToStage(CHECKOUT_STAGES.BILLING_OPTIONS);
  }

  static onBillingDataSubmit(billingData, paymentType, bank = null) {
    CheckoutStore.useBillingData(billingData, paymentType, bank);
    CheckoutCoordinator.goToStage(CHECKOUT_STAGES.CONFIRM_CHECKOUT);
  }

  static async onCheckoutConfirm(additionalNotes) {
    return new Promise(async (resolve, reject) => {
      await CheckoutStore.confirmCheckout(additionalNotes);
      if (!CheckoutStore.creationError) {
        const checkoutData = CheckoutStore.finishedCheckout;
        let paymentName = checkoutData.paymentTypeName;
        if (checkoutData.paymentType === 4) {
          paymentName = `${paymentName} - ${checkoutData.bank.name}`;
        }
        GA4.pursacheEvent(
          checkoutData.id,
          checkoutData.totalPrice,
          checkoutData.shippingData ? checkoutData.totalPrice - checkoutData.totalProductPrice : 0,
          paymentName,
          checkoutData.cart.map(x => ({
            item_name: x.product.name,
            item_id: x.product.id,
            price: x.product && x.product.financing && x.product.financing.price,
            quantity: x.amount,
            brand: x.product.brand,
          })),
          SessionStore.user.id,
        );
        resolve(true);
      }
      reject(new Error('No se pudo finalizar la compra'));
    });
  }


  /*
    Determines, given the data in CheckoutStore, which is the
    state the user *should* be in at this moment. This function is useful to handle
    the case where users go back and forth using the browser's navigation arrows.
  */
  static getCorrectStage() {
    const hasArticleInPresale = CheckoutStore.checkoutCart
      .map(item => item.product.id)
      .some(item => ConfigParametersStore.isValueInFeatureFlagData('product_next_deliveries', item));
    if (CheckoutStore.finishedCheckout && !CheckoutStore.bank) {
      return CHECKOUT_STAGES.CHECKOUT_SUCCESS;
    } else if (CheckoutStore.billingData && CheckoutStore.paymentType) {
      return CHECKOUT_STAGES.CONFIRM_CHECKOUT;
    } else if (
      (CheckoutStore.pickupChosen && CheckoutStore.firstPhone) || (
        CheckoutStore.selectedOption && (
          !CheckoutStore.cartHasInstallations ||
          CheckoutStore.selectedInstallationOptions || hasArticleInPresale
        )
      )
    ) {
      return CHECKOUT_STAGES.BILLING_OPTIONS;
    } else if (CheckoutStore.selectedOption) {
      return CHECKOUT_STAGES.INSTALLATION_OPTIONS;
    } else if (!CheckoutStore.pickupChosen && CheckoutStore.selectedAddress) {
      return CHECKOUT_STAGES.SHIPPING_OPTIONS;
    } else if (CheckoutStore.pickupChosen && !CheckoutStore.selectedAddress) {
      return CHECKOUT_STAGES.SHIPPING_OPTIONS;
    }
    return CHECKOUT_STAGES.CHOOSE_ADDRESS;
  }

  componentDidMount() {
    CheckoutStore.startCheckout();
    SessionStore.getUser();
  }

  /*
    On each update, this component must check that the correct checkout step is being shown.
    Also, when entering a certain step, the store must be updated to delete information
    used on future steps. This is needed to support going back and forth.
  */
  componentDidUpdate(prevProps) {
    const currentCheckoutStep = this.props.match.params.checkoutStep;
    // If the step has changed, perform all necessary checks and updates.
    if (currentCheckoutStep !== prevProps.match.params.checkoutStep) {
      if (currentCheckoutStep === CHECKOUT_STAGES.CHOOSE_ADDRESS) {
        CheckoutStore.clearAddressData();
        CheckoutStore.clearShippingOptionData();
        CheckoutStore.clearInstallationData();
        CheckoutStore.clearPhoneData();
        CheckoutStore.clearBillingData();
      } else if (currentCheckoutStep === CHECKOUT_STAGES.SHIPPING_OPTIONS) {
        CheckoutStore.clearShippingOptionData();
        CheckoutStore.clearInstallationData();
        CheckoutStore.clearPhoneData();
        CheckoutStore.clearBillingData();
      } else if (currentCheckoutStep === CHECKOUT_STAGES.INSTALLATION_OPTIONS) {
        CheckoutStore.clearInstallationData();
        CheckoutStore.clearBillingData();
      } else if (currentCheckoutStep === CHECKOUT_STAGES.BILLING_OPTIONS) {
        CheckoutStore.clearBillingData();
      } else if (currentCheckoutStep === CHECKOUT_STAGES.CONFIRM_CHECKOUT) {
        CheckoutStore.clearCreationError();
      }
      const correctStage = CheckoutCoordinator.getCorrectStage();
      if (correctStage !== currentCheckoutStep) {
        CheckoutCoordinator.goToStage(correctStage);
      }
    }
  }

  _renderCurrentStage() {
    // If the checkout has been completed, render the checkout success page
    if (CheckoutStore.finishedCheckout && !CheckoutStore.bank) {
      return <CheckoutSuccess />;
    }
    switch (this.props.match.params.checkoutStep) {
      case CHECKOUT_STAGES.CHOOSE_ADDRESS:
        return (
          <ChooseAddress
            onAddressSelection={CheckoutCoordinator.onAddressSelection}
            onPickupSelection={CheckoutCoordinator.onPickupSelection}
          />
        );
      case CHECKOUT_STAGES.SHIPPING_OPTIONS:
        return (
          <ShippingOptions onSubmit={CheckoutCoordinator.onShippingOptionSubmit} />
        );
      case CHECKOUT_STAGES.INSTALLATION_OPTIONS:
        return (
          <InstallationOptions onSubmit={CheckoutCoordinator.onInstallationSubmit} />
        );
      case CHECKOUT_STAGES.BILLING_OPTIONS:
        return (
          <PaymentMethodsAndBilling onSubmit={CheckoutCoordinator.onBillingDataSubmit} />
        );
      case CHECKOUT_STAGES.CONFIRM_CHECKOUT:
        return (
          <ConfirmCheckout
            onSubmit={CheckoutCoordinator.onCheckoutConfirm}
          />
        );
      case CHECKOUT_STAGES.CHECKOUT_SUCCESS:
        return (
          <CheckoutSuccess />
        );
      default:
        return null;
    }
  }

  render() {
    if (!SessionStore.loggedIn || !CartStore.cart || !CartStore.cart.length) {
      return (
        <AppRedirect routeName={routeNaming.HOME} />
      );
    }
    if (!this.props.match.params.checkoutStep) {
      return null;
    }
    return (
      <div className={styles.container}>
        <CheckoutNavbar currentStage={this.props.match.params.checkoutStep} />
        {this._renderCurrentStage()}
      </div>
    );
  }
}

CheckoutCoordinator.propTypes = IPropTypes;

const ObservedCheckoutCoordinator = observer(CheckoutCoordinator);

export { ObservedCheckoutCoordinator as CheckoutCoordinator };



// WEBPACK FOOTER //
// ./src/pages/checkout-coordinator/CheckoutCoordinator.jsx